import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { LineRequestDto, LineResponseDto, ProjectCategoryDto } from './types'
import { addQuoteVersionLineV2, getProjectQuoteCategoriesV2, updateQuoteVersionLineV2 } from './quotes-v2.api'
import { pickableCategories } from './category-options'

interface NoteLineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quoteId: string | null
  versionId: string | null
  mode: 'create' | 'update'
  lineId?: string | null
  initialLine?: LineResponseDto | null
  onSubmitted: () => void | Promise<void>
}

export function NoteLineDialog({
  open,
  onOpenChange,
  projectId,
  quoteId,
  versionId,
  mode,
  lineId,
  initialLine,
  onSubmitted,
}: NoteLineDialogProps) {
  const [categories, setCategories] = useState<ProjectCategoryDto[]>([])
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    let isMounted = true

    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const categoriesResponse = await getProjectQuoteCategoriesV2(projectId)
        if (!isMounted) return

        const cats = categoriesResponse.categories || []
        setCategories(cats)

        if (mode === 'update') {
          setTitle(typeof initialLine?.title === 'string' ? initialLine.title : '')
          setDescription(typeof initialLine?.description === 'string' ? initialLine.description : '')
          setCategoryId(initialLine?.categoryId ?? null)
        } else {
          setTitle('')
          setDescription('')
          setCategoryId(null)
        }
      } catch {
        if (isMounted) {
          setError('Erreur lors du chargement')
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [open, projectId, mode, initialLine?.title, initialLine?.description, initialLine?.categoryId])

  const selectedCategory = useMemo(() => {
    if (!categoryId) return null
    return categories.find((c) => c.id === categoryId) || null
  }, [categories, categoryId])

  const canSubmit =
    !!quoteId &&
    !!versionId &&
    !isLoading &&
    !isSubmitting &&
    (mode === 'create' || !!lineId)

  const handleSubmit = async () => {
    if (!quoteId || !versionId) {
      setError('Erreur: contexte du devis manquant')
      return
    }
    if (mode === 'update' && !lineId) {
      setError('Erreur: ligne introuvable')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const trimmedTitle = title.trim()
      const trimmedDescription = description.trim()

      const request: LineRequestDto = {
        kind: 'note',
        title: trimmedTitle,
        description: trimmedDescription ? trimmedDescription : null,
        categoryId: categoryId,
        categoryLabel: selectedCategory?.label ?? null,
        familyId: null,
        familyKey: null,
        attributes: null,
        quantity: null,
        unitPrice: null,
        vatPercent: null,
        amount: null,
      }

      if (mode === 'update' && lineId) {
        await updateQuoteVersionLineV2(projectId, quoteId, versionId, lineId, request)
      } else {
        await addQuoteVersionLineV2(projectId, quoteId, versionId, request)
      }

      await onSubmitted()
      onOpenChange(false)
    } catch {
      setError(mode === 'update' ? 'Erreur lors de la mise à jour de la note' : "Erreur lors de l'ajout de la note")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{mode === 'update' ? 'Modifier la note' : 'Ajouter une note'}</DialogTitle>
          <DialogDescription>Ajoute une ligne de type note (sans montant). Tous les champs sont optionnels.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Chargement...</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={categoryId ?? '__all__'} onValueChange={(v) => setCategoryId(v === '__all__' ? null : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Toutes catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Toutes catégories</SelectItem>
                  {pickableCategories(categories, initialLine?.categoryId).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Titre</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre (optionnel)" />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optionnelle)"
                rows={4}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? (mode === 'update' ? 'Enregistrement...' : 'Ajout...') : mode === 'update' ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
